import React, { useEffect, useMemo, useRef, useState } from "react";
import { ShippingHandleType, CheckoutActions } from "lib/types/checkout";
import { useLocale } from "lib/hooks/useLocale";
import { postRequest } from "lib/core/apiClient";
import { appendParamsToItemsUrl, getDefaultShippingHandle } from "lib/utils/checkout";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { mutate } from "swr";
import { getUpsellUrl } from "lib/utils/upsell";
import { analyticsEvents } from "lib/utils/constants";
import Price from "components/common/Price";
import { redirectUrl, classNames } from "lib/utils/helpers";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { Truck, CaretDown } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { useUserContext } from "lib/contexts/UserProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { errorToast } from "lib/utils/toasters";
import { currencyFormatter } from "lib/utils/formatters";
import { motion, AnimatePresence } from "motion/react";
import ShippingHandle from "./ShippingHandle";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";

interface ShippingHandlesSectionProps {
  loadPayments: () => void;
}

const ShippingHandlesSection: React.FC<ShippingHandlesSectionProps> = ({ loadPayments }) => {
  const { t } = useLocale();
  const {
    state: {
      shippingHandles,
      checkoutId,
      checkoutView,
      actionUrls,
      activeComponent,
      appliedCoupons,
      coupons,
    },
    actions: { updateCheckoutBasedOnCheckoutResponse, setShippingHandles, setActiveComponent },
  } = useCheckoutContext();
  const {
    state: { user },
  } = useUserContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const {
    actions: { mutatePayment },
  } = usePaymentContext();
  const {
    state: { checkoutMetadata, merchant },
  } = useMerchantContext();
  const { state: { isC2P } } = useCheckoutContext();
  const { initialCheckoutStep } = checkoutMetadata ?? {};

  const hasCoupons = coupons.find((coupon) => coupon?.coupon_details?.coupon_type === "SHIPPING");
  const hasAppliedShippingCoupon = useMemo(() => Boolean(
    appliedCoupons?.find((coupon) => coupon?.couponType === "SHIPPING"),
  ), [appliedCoupons]);

  const [selectedHandle, setSelectedHandle] = useState<ShippingHandleType>({
    id: "",
    handle_name: "",
    price: "",
    selected_handle: false,
    cod_enabled: false,
    cod_extra_price: "0",
    online_enabled: true,
    original_price: "",
  });
  const [isExpanded, setIsExpanded] = useState(
    checkoutMetadata?.uiAttributes?.shippingListExpansion && initialCheckoutStep === "PAYMENTS",
  );
  const [isUpdatingShipping, setIsUpdatingShipping] = useState(false);
  const shippingAccordionRef = useRef<any>(null);

  useEffect(() => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_SHIPPING_HANDLES_LOADED,
      eventType: "load",
      metaData: {
        shippingData: {
          availableHandles: shippingHandles,
        },
      },
    });
    const defaultShippingHandle: ShippingHandleType | null =
      getDefaultShippingHandle(shippingHandles) ?? null;

    // Select the first shipping handle by default if no handle is selected by default
    if (defaultShippingHandle) {
      const isDefaultHandleSelected = defaultShippingHandle.selected_handle;
      if (!isDefaultHandleSelected) {
        onHandleSelect(defaultShippingHandle, true);
      } else {
        loadPayments();
      }
    } else {
      loadPayments();
    }
  }, []);

  useEffect(() => {
    if (activeComponent === "SHIPPING_HANDLES_SECTION") {
      setIsExpanded(true);
      shippingAccordionRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    return () => {
      setActiveComponent("NONE");
    };
  }, [activeComponent]);

  useEffect(() => {
    if (!Boolean(shippingHandles.length)) {
      return;
    }
    const defaultShippingHandle: ShippingHandleType | undefined = getDefaultShippingHandle(shippingHandles);
    if (defaultShippingHandle) {
      setSelectedHandle(defaultShippingHandle);
    }
  }, [shippingHandles]);

  const onHandleSelect = async (handle: ShippingHandleType, initialLoad: boolean = false) => {
    if (!handle.id) return;
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_SHIPPING_HANDLE_SELECTED,
      eventType: "click",
      metaData: {
        shippingData: {
          availableHandles: shippingHandles,
          selectedHandle: handle,
          isCod: false,
        },
      },
    });

    await updateShippingHandle(handle, initialLoad);
    if (initialLoad) loadPayments();
    return;
  };

  const updateShippingHandle = async (handle: ShippingHandleType, initialLoad: boolean = false) => {
    const payload = {
      shipping_handle: handle.id,
    };
    const currentSelectedHandle = selectedHandle;
    const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);

    try {
      setIsUpdatingShipping(true);
      setSelectedHandle(handle);
      const response = await postRequest(
        `/checkout/${checkoutId}/shipping-handle`,
        payload,
        "KRATOS_PRIVATE",
      );
      handleCheckoutResponse(response);
      const selectedShippingHandle: ShippingHandleType | undefined = getDefaultShippingHandle(
        response?.metadata?.available_shipping_handles,
      );
      mutate(DISCOUNT_PATH);
      if (selectedShippingHandle) {
        setSelectedHandle(selectedShippingHandle);
      } else {
        setSelectedHandle(currentSelectedHandle);
        errorToast(t("something_went_wrong"));
      }
      if (checkoutView === "PAYMENTS" && !initialLoad) {
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        if (Boolean(checkoutMetadata?.uiAttributes?.upsell?.isEnabled))
          mutate(getUpsellUrl({ checkoutId }));
        mutate(`UPI_INTENT`);
        return;
      }

      if (response?.metadata?.action_urls?.[CheckoutActions.ADDRESS_SELECT]?.success_url) {
        redirectUrl(response.metadata.action_urls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }

      if (
        actionUrls &&
        actionUrls[CheckoutActions.ADDRESS_SELECT] &&
        actionUrls[CheckoutActions.ADDRESS_SELECT].success_url
      ) {
        redirectUrl(actionUrls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }
    } catch (e) {
      console.error(e);
      setSelectedHandle(currentSelectedHandle);
      errorToast(t("something_went_wrong"));
      if (
        actionUrls &&
        actionUrls[CheckoutActions.ADDRESS_SELECT] &&
        actionUrls[CheckoutActions.ADDRESS_SELECT].failure_url
      ) {
        redirectUrl(actionUrls[CheckoutActions.ADDRESS_SELECT].failure_url);
      }
    } finally {
      setIsUpdatingShipping(false);
    }
  };

  const handleCheckoutResponse = (checkoutResponse: any) => {
    updateCheckoutBasedOnCheckoutResponse(checkoutResponse);
    const updatedHandles = checkoutResponse?.metadata?.available_shipping_handles ?? [];
    if (Boolean(updatedHandles?.length)) {
      setShippingHandles(updatedHandles);
    }
  };

  const renderShippingDiscountStatus = () => {
    if (isUpdatingShipping) {
      return (
        <>
          <div className="flex items-center gap-2">
            <div className="h-3 w-16 animate-pulse rounded bg-zinc-200"></div>
          </div>
          <OverlaySpinner />
        </>
      );
    }

    if (hasAppliedShippingCoupon) {
      const originalPrice = parseFloat(selectedHandle?.original_price || "0");
      const currentPrice = parseFloat(selectedHandle?.price || "0");
      const discountAmount = originalPrice - currentPrice;

      const formattedPrice = currencyFormatter(discountAmount);

      return (
        <p className="text-xs font-medium text-yay-dark flex gap-1">
          <span> {t("saved")} </span> <span className="text-xs">{formattedPrice}</span>
        </p>
      );
    }

    if (hasCoupons) {
      return <p className="text-xs font-medium text-yay-dark">{t("shipping_discount_available")}</p>;
    }

    return null;
  };

  if (
    !Boolean(user?.addresses?.length) ||
    !Boolean(shippingHandles.length) ||
    checkoutMetadata?.uiAttributes?.layout?.hiddenElements?.includes("SHIPPING_HANDLES_SECTION") || isC2P
  )
    return <></>;

  return (
    <div id="shipping-handles-section" ref={shippingAccordionRef}>
      <div className="rounded-xl border border-gray-light bg-white">
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex cursor-pointer items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <Icon
              IconComponent={Truck}
              size="md"
              className="text-coal-dark"
              iconProps={{ strokeWidth: 2.5 }}
            />
            <div className="flex flex-col items-start gap-0.5">
              <h2 className="text-sm font-medium text-coal-dark">{t("shipping")}</h2>
              {renderShippingDiscountStatus()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {Number(selectedHandle?.price) < 1 ? (
              <p className="text-sm font-medium uppercase text-yay-dark">{t("free_shipping")}</p>
            ) : (
              <div className="flex items-center gap-1">
                <Price
                  total={parseFloat(selectedHandle?.price)}
                  compareAt={
                    hasAppliedShippingCoupon ? parseFloat(selectedHandle?.original_price || "0") : undefined
                  }
                  orientation="horizontal"
                  customCSS="flex w-full"
                />
              </div>
            )}
            <Icon
              IconComponent={CaretDown}
              size="xs"
              className={classNames(
                "text-zinc-500 transition-transform duration-200 ease-out",
                isExpanded ? "rotate-180" : "rotate-0",
              )}
              iconProps={{ weight: "bold" }}
            />
          </div>
        </div>

        <AnimatePresence initial={false} mode="wait">
          {isExpanded && (
            <motion.div
              key="shipping-handles-list"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 30,
                mass: 1,
              }}
              className="overflow-hidden">
              <div className="pb-4 pt-2">
                <ul className="flex flex-col gap-3">
                  {shippingHandles.map((handle) => (
                    <li
                      key={handle?.id}
                      onClick={(e) => {
                        e.preventDefault();
                        onHandleSelect(handle);
                        setIsExpanded(false);
                      }}>
                      <ShippingHandle
                        id={handle?.id}
                        name={`${handle.handle_name}`}
                        charge={handle.price}
                        originalPrice={handle.original_price}
                        isChecked={selectedHandle.id === handle?.id}
                        etdText={handle.etd_text}
                        minDays={handle.min_days_to_deliver}
                        maxDays={handle.max_days_to_deliver}
                        codEnabled={handle.cod_enabled}
                        codExtraPrice={parseInt(handle.cod_extra_price ?? "0")}
                        onlineEnabled={handle.online_enabled}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ShippingHandlesSection;
